import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  Drawer,
  ButtonBase,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ChangePasswordModal from '../modals/ChangePasswordModal';
import EditUserDetailsModal from '../modals/EditUserDetailsModal';
import { getNavItems, isNavItemActive, SIDEBAR_WIDTH } from './navConfig';
import { useSidebar } from './SidebarContext';
import { neonBlue, stormGrey } from '../../theme/colors';
import { clearSessionLogoutTimer, forceLogout } from '../../utils/sessionAuth';

const MobileNav = ({ open, onClose }) => {
  const location = useLocation();
  const userRole = localStorage.getItem('userRole');
  const companyName = localStorage.getItem('companyName') || 'Elite Riders';
  const navItems = getNavItems(userRole);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: SIDEBAR_WIDTH } }}
    >
      <Stack spacing={2} sx={{ p: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.contrastText',
            }}
          >
            <LocalShippingIcon />
          </Box>
          <Box>
            <Typography variant="subtitle2">{companyName}</Typography>
            <Typography variant="caption" color="text.secondary">
              Delivery Management
            </Typography>
          </Box>
        </Stack>
      </Stack>
      <Divider />
      <Box sx={{ px: 2, py: 2 }}>
        <Stack spacing={0.5}>
          {navItems.map((item) => {
            const active = isNavItemActive(location.pathname, item.href);
            const Icon = item.icon;

            return (
              <ButtonBase
                key={item.key}
                component={RouterLink}
                to={item.href}
                onClick={onClose}
                sx={{
                  alignItems: 'center',
                  borderRadius: 1,
                  display: 'flex',
                  gap: 1.5,
                  justifyContent: 'flex-start',
                  px: 1.5,
                  py: 1.25,
                  width: '100%',
                  color: active ? neonBlue[600] : stormGrey[600],
                  bgcolor: active ? neonBlue[50] : 'transparent',
                }}
              >
                <Icon fontSize="small" />
                <Typography variant="body2" fontWeight={active ? 600 : 500}>
                  {item.title}
                </Typography>
              </ButtonBase>
            );
          })}
        </Stack>
      </Box>
    </Drawer>
  );
};

const MainNav = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const { collapsed, toggleCollapsed } = useSidebar();

  const username = localStorage.getItem('username') || 'User';
  const companyName = localStorage.getItem('companyName') || 'Elite Riders';
  const userRole = localStorage.getItem('userRole');

  const handleLogout = async () => {
    setAnchorEl(null);
    clearSessionLogoutTimer();
    await forceLogout('manual');
  };

  const initials = username
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <>
      <Box
        component="header"
        sx={{
          bgcolor: 'background.paper',
          borderBottom: `1px solid ${stormGrey[200]}`,
          position: 'sticky',
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 52,
            px: { xs: 1.5, lg: 2 },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{ display: { lg: 'none' } }}
              aria-label="Open navigation menu"
            >
              <MenuIcon />
            </IconButton>
            <IconButton
              onClick={toggleCollapsed}
              sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
            <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {companyName}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'primary.main',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {initials}
              </Avatar>
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuItem disabled>
          <Box>
            <Typography variant="subtitle2">{username}</Typography>
            <Typography variant="caption" color="text.secondary">
              {userRole} · {companyName}
            </Typography>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setShowEditProfileModal(true);
          }}
        >
          Edit profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setShowPasswordModal(true);
          }}
        >
          Change password
        </MenuItem>
        <MenuItem onClick={handleLogout}>Sign out</MenuItem>
      </Menu>

      <MobileNav open={mobileOpen} onClose={() => setMobileOpen(false)} />

      <EditUserDetailsModal
        open={showEditProfileModal}
        onClose={() => setShowEditProfileModal(false)}
      />
      <ChangePasswordModal
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </>
  );
};

export default MainNav;
